'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '@/lib/api';
import { TERMS, plural } from '@/lib/copy';
import {
  COMMIT_TIMEOUT_MESSAGE,
  LAUNCH_REQUEST_TIMEOUT_MS,
  LAUNCH_TIMEOUT_MESSAGE,
  isLaunchRequestTimeout,
  launchAdvisoryNote,
} from '@/lib/launch-request';
import { ORBIT_LAUNCHED_HEADLINE, orbitLaunchWarning } from '@/lib/copy/mission';
import { utcInputValueToIso } from './missionDetailShared';
import type { MissionType } from '@/lib/mission-type';
import {
  draftToWaypointPayload,
  type CanvasGates,
  type DraftWaypoint,
  type NowLaunchGate,
  type WhenMode,
} from './missionCanvasShared';

/**
 * Ceiling on the ordinary writes this flow makes on the way to a launch.
 *
 * Shorter than the launch itself, which waits on dock hardware; these are plain CRUD against
 * the API. Bounded for the same reason the launch is: every one of them runs inside the same
 * `try` under the same `saving` flag, so ANY of them hanging leaves LAUNCH, CREATE and Cancel
 * disabled behind "Saving…" with no way out but a reload — which destroys the drawn route.
 * Bounding only the launch POST would have left three other doors to the same dead end.
 */
const COMMIT_REQUEST_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Committing a drawn mission: create it, save its route, and — in the NOW lane —
// launch it.
//
// Extracted from MissionCanvas when the COP gained its own compose surface. Not
// for tidiness: this is the path that ends with an aircraft leaving the ground,
// and the failure handling below is the accumulated answer to a series of real
// defects (a retry that flew a deselected airframe over a route the map no
// longer showed; a route that silently did not persist; a mission created with
// no id to address it by). A second copy on the COP would have been a second
// place for those to come back.
//
// The surfaces differ in how they LOOK, not in what they command. Everything
// here is surface-agnostic: no layout, no map, no navigation — the caller says
// what to do once a mission exists.
// ---------------------------------------------------------------------------

/**
 * How long an armed LAUNCH ignores a confirm press.
 *
 * Two presses of one button are not a safeguard on their own: arm and confirm
 * are the same control in the same position, so an ordinary double-click — what
 * an operator does to every other row on the board — armed and commanded a real
 * takeoff in one gesture. Matches `CopMissionsPanel`'s dwell and the commit
 * strip's; the hazard is identical wherever a launch is armed.
 */
export const LAUNCH_ARM_DWELL_MS = 600;

/** The mission being committed, with the sentinels already resolved to null. */
export interface MissionCommitDraft {
  name: string;
  siteId: string | null;
  droneId: string | null;
  pilotId: string | null;
  altitudeM: number;
  waypoints: DraftWaypoint[];
  /** 022. What the mission IS. Always sent, so the row never relies on the column default. */
  missionType: MissionType;
  /** Set only while the type is `orbit`; ignored otherwise. */
  orbit: { radiusM: number; direction: 'cw' | 'ccw' } | null;
}

export interface UseMissionCommitArgs {
  /** Read at call time, never captured — the operator can change anything up to the press. */
  draft: MissionCommitDraft;
  gates: CanvasGates;
  nowGate: NowLaunchGate;
  whenMode: WhenMode;
  /** The SCHEDULE lane's departure, as a UTC datetime-local input value. '' = none. */
  scheduledAt: string;
  /** A mission now exists and the surface is done with it. */
  onCreated: (missionId: string) => void;
  /** The write was accepted but no id came back — nothing can be addressed. */
  onCreatedWithoutId: () => void;
  /** Navigate to a mission (the toast's Open action). */
  onOpenMission: (missionId: string) => void;
}

export interface MissionCommitController {
  saving: boolean;
  /** The server's own refusal, verbatim — the GO/NO-GO reason the operator has to act on. */
  launchError: string | null;
  /**
   * Set when the mission was created but its route was not. The surface must stay
   * open on this: the drawn route exists only in component state, so leaving
   * destroys work that cannot be recovered.
   */
  routeRescueMissionId: string | null;
  /**
   * A NOW launch that got as far as a saved mission with a saved route, then was
   * refused or failed. Nothing is lost — it is a draft on the board with its
   * route — so a retry launches THAT mission rather than creating a second one.
   */
  launchRescueMissionId: string | null;
  launchArmed: boolean;
  create: () => Promise<void>;
  /** First press arms, second (after the dwell) commands. */
  launchNow: () => Promise<void>;
  retryRoute: () => Promise<void>;
  disarmLaunch: () => void;
}

export function useMissionCommit({
  draft,
  gates,
  nowGate,
  whenMode,
  scheduledAt,
  onCreated,
  onCreatedWithoutId,
  onOpenMission,
}: UseMissionCommitArgs): MissionCommitController {
  const [saving, setSaving] = useState(false);
  const [launchArmedAt, setLaunchArmedAt] = useState<number | null>(null);
  const [createdMissionId, setCreatedMissionId] = useState<string | null>(null);
  const [launchTargetId, setLaunchTargetId] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Everything the operator can still change is read through refs at press time.
  // Capturing it in a callback dependency would let a stale route or a stale
  // aircraft be the thing that actually flies.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const gatesRef = useRef(gates);
  gatesRef.current = gates;
  const nowGateRef = useRef(nowGate);
  nowGateRef.current = nowGate;
  const whenModeRef = useRef(whenMode);
  whenModeRef.current = whenMode;
  const scheduledAtRef = useRef(scheduledAt);
  scheduledAtRef.current = scheduledAt;
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  const onCreatedWithoutIdRef = useRef(onCreatedWithoutId);
  onCreatedWithoutIdRef.current = onCreatedWithoutId;
  const onOpenMissionRef = useRef(onOpenMission);
  onOpenMissionRef.current = onOpenMission;

  const openAction = useCallback(
    (missionId: string) => ({
      action: { label: 'Open', onClick: () => onOpenMissionRef.current(missionId) },
    }),
    [],
  );

  /** The waypoint PUT, shared by the initial create and the retry. */
  const saveRoute = useCallback(async (missionId: string) => {
    const { waypoints, altitudeM } = draftRef.current;
    await api.put(`/api/missions/${missionId}/waypoints`, {
      waypoints: draftToWaypointPayload(waypoints, altitudeM),
    }, { signal: AbortSignal.timeout(COMMIT_REQUEST_TIMEOUT_MS) });
  }, []);

  /**
   * The mission row itself, shared by both lanes. Returns the new id, or null when
   * the API accepted the write but gave us nothing to address it by.
   */
  const createMission = useCallback(async (departureIso: string | undefined): Promise<string | null> => {
    const { name, siteId, droneId, pilotId, altitudeM, missionType, orbit } = draftRef.current;
    const created = await api.post<{ data?: { id?: string } }>('/api/missions', {
      name: name.trim(),
      // Omitted keys leave the column at its default; a chosen record sends its id.
      // There is no "clear" case on create, so an untouched select sends nothing.
      ...(siteId !== null ? { site_id: siteId } : {}),
      ...(droneId !== null ? { assigned_drone_id: droneId } : {}),
      ...(pilotId !== null ? { assigned_pilot_id: pilotId } : {}),
      ...(departureIso ? { scheduled_at: departureIso } : {}),
      default_altitude: altitudeM,
      // 022. The orbit parameters go ONLY with an orbit. 022's coherence constraint refuses a
      // radius on any other type, and sending one that the panel merely still had in state
      // would turn a type switch into a 400 the operator did not cause.
      type: missionType,
      ...(missionType === 'orbit' && orbit
        ? { orbit_radius_m: orbit.radiusM, orbit_direction: orbit.direction }
        : {}),
    }, { signal: AbortSignal.timeout(COMMIT_REQUEST_TIMEOUT_MS) });
    const newId = created.data?.id;
    return typeof newId === 'string' && newId.length > 0 ? newId : null;
  }, []);

  const retryRoute = useCallback(async () => {
    if (!createdMissionId) return;
    setSaving(true);
    try {
      await saveRoute(createdMissionId);
      const count = draftRef.current.waypoints.length;
      // In the NOW lane the route was only ever a step towards a takeoff — hand back
      // to the launch control rather than navigating away from a mission that has
      // not flown.
      if (whenModeRef.current === 'now') {
        setLaunchTargetId(createdMissionId);
        setCreatedMissionId(null);
        toast.success(`Route saved — ${count} waypoints. Confirm the launch.`);
        return;
      }
      toast.success(`Route saved — ${count} waypoints`);
      onCreatedRef.current(createdMissionId);
    } catch (err) {
      toast.error(isLaunchRequestTimeout(err)
        ? `Route still not saved — ${COMMIT_TIMEOUT_MESSAGE}`
        : `Route still not saved: ${getApiErrorMessage(err, 'unknown error')}`);
    } finally {
      setSaving(false);
    }
  }, [createdMissionId, saveRoute]);

  /**
   * The NOW lane: create, save the route, launch. No draft → scheduled step at all —
   * the mission goes straight to in_progress on the server, which is the only
   * transition that commands an aircraft.
   *
   * Each stage leaves the operator somewhere recoverable. A failed route keeps the
   * drawn route in state (it exists nowhere else); a refused or failed launch keeps
   * the mission id so the retry launches THAT mission instead of creating a second.
   */
  const launchNow = useCallback(async () => {
    if (!nowGateRef.current.canLaunch) {
      toast.error(nowGateRef.current.blockers[0] ?? 'Cannot launch this mission');
      return;
    }
    if (launchArmedAt === null) {
      setLaunchArmedAt(Date.now());
      return;
    }
    // The dwell. A confirm that lands within the double-click window is the same
    // gesture as the arm, not a decision — so it is swallowed and the button stays
    // armed for a real second press. Silent on purpose: a toast here would be noise
    // for the operator who simply double-clicked, and the button visibly remains armed.
    if (Date.now() - launchArmedAt < LAUNCH_ARM_DWELL_MS) return;
    setLaunchArmedAt(null);
    setSaving(true);
    setLaunchError(null);
    try {
      let missionId = launchTargetId;
      if (missionId === null) {
        // A launch happens now, so the mission carries now as its departure —
        // otherwise the board's Time column reads "-" for a sortie that is in the air.
        const newId = await createMission(new Date().toISOString());
        if (newId === null) {
          toast.warning('Mission created, but no id came back — it was not launched and the route was not saved');
          onCreatedWithoutIdRef.current();
          return;
        }
        try {
          await saveRoute(newId);
        } catch (routeErr) {
          // Nothing has been commanded. Stay put with the drawn route intact.
          setCreatedMissionId(newId);
          toast.error(isLaunchRequestTimeout(routeErr)
            ? `Mission created, and nothing was launched. ${COMMIT_TIMEOUT_MESSAGE}`
            : `Mission created, but the route failed to save — nothing was launched: ${getApiErrorMessage(routeErr, 'unknown error')}`);
          return;
        }
        missionId = newId;
        setLaunchTargetId(newId);
      } else {
        // A RETRY commands a mission that already exists, and the operator has had
        // every opportunity to change their mind in between — that is the whole point
        // of the retry. Refused for "Drone battery at 12%", they pick a different
        // aircraft in the rail and the row visibly becomes checked; redraw the route,
        // raise the altitude. Going straight to the launch POST flew the SAVED copy:
        // the airframe they had deliberately deselected, over the route the map no
        // longer showed. If that aircraft's battery had ticked past 25% in the
        // meantime, it took off.
        //
        // So push what is on screen before commanding anything — the saved mission is
        // only ever allowed to be what the operator can see. Explicit nulls rather
        // than omitted keys, because an unassignment has to travel too.
        //
        // Ask what state the mission is actually in BEFORE rewriting it.
        // `launchTargetId` is set as soon as create+saveRoute succeed and never
        // cleared, so a retry is offered for any failure of the launch POST —
        // including a transport failure of a request the server completed. Syncing
        // first would then repoint the aircraft and replace the route of a sortie that
        // is already airborne: the route write has no status guard the operator can
        // see, and `assigned_drone_id` is the column flight close-out binds through.
        try {
          const current = await api.get<{ data: { status?: string } }>(
            `/api/missions/${missionId}`,
            { signal: AbortSignal.timeout(COMMIT_REQUEST_TIMEOUT_MS) },
          );
          const status = current.data?.status;
          if (status !== 'draft' && status !== 'scheduled') {
            const message = status === 'in_progress'
              ? 'This mission is already in the air — it was launched. Nothing was changed.'
              : `This mission is ${status ?? 'no longer available'} and cannot be launched. Nothing was changed.`;
            setLaunchError(message);
            toast.error(message, openAction(missionId));
            return;
          }
        } catch (readErr) {
          const message = `Could not confirm the mission's state — nothing was launched: ${getApiErrorMessage(readErr, 'unknown error')}`;
          setLaunchError(message);
          toast.error(message);
          return;
        }

        try {
          const { name, siteId, droneId, pilotId, altitudeM, missionType, orbit } = draftRef.current;
          await api.put(`/api/missions/${missionId}`, {
            name: name.trim(),
            site_id: siteId,
            assigned_drone_id: droneId,
            assigned_pilot_id: pilotId,
            default_altitude: altitudeM,
            // 022. The TYPE has to travel with the rest of the re-sync, or this PUT breaks its
            // own invariant: after a refused launch an operator can change FOLLOW ROUTE to
            // OUT & BACK, press launch again, and the stored row still says FOLLOW ROUTE — so
            // the aircraft flies the old type's finish action. Sending everything BUT the type
            // is the one combination that looks synced and is not.
            type: missionType,
            // Cleared explicitly when the type is no longer an orbit: 022's coherence
            // constraint refuses a radius on any other type, so leaving the old values behind
            // would make this PUT fail rather than merely be stale.
            orbit_radius_m: missionType === 'orbit' && orbit ? orbit.radiusM : null,
            orbit_direction: missionType === 'orbit' && orbit ? orbit.direction : null,
          }, { signal: AbortSignal.timeout(COMMIT_REQUEST_TIMEOUT_MS) });
          await saveRoute(missionId);
        } catch (syncErr) {
          // Nothing has been commanded. The saved mission still holds the PREVIOUS
          // plan, so stop here rather than let the next press fly something the
          // operator has moved on from.
          const detail = getApiErrorMessage(syncErr, 'unknown error');
          const message = `Could not update the saved mission — nothing was launched: ${detail}`;
          setLaunchError(message);
          toast.error(message);
          return;
        }
      }

      const launchRes = await api.post<{ data?: { advisories?: unknown } }>(`/api/missions/${missionId}/launch`, {}, {
        // Bounded for the reason the queue panel's door is: without a ceiling a
        // black-holed request leaves `saving` true forever, and every control on
        // this panel — LAUNCH, CREATE, Cancel — stays disabled behind "Saving…".
        // The operator's only way out is a reload, which throws away a drawn
        // route that exists nowhere but this component's state.
        signal: AbortSignal.timeout(LAUNCH_REQUEST_TIMEOUT_MS),
      });
      // Advisory conflicts ride along with the success, for the reason the queue panel's door
      // carries them: a same-aircraft clash with a scheduled mission no longer refuses, so
      // nothing else would ever mention that this launch just orphaned another operator's plan.
      const advisory = launchAdvisoryNote(launchRes?.data?.advisories);
      // AN ORBIT GETS ITS OWN HEADLINE AND ITS OWN DESCRIPTION (#700 review HIGH).
      //
      // It uploads no wayline, so a waypoint count is untrue about what went on the wire AND
      // reads exactly like the GO TO POINT this is not. And this is the last moment anything can
      // tell the operator the sortie will not end on its own - see `orbitLaunchWarning`.
      //
      // The orbit warning goes FIRST when a conflict advisory is also present: the advisory is
      // about someone else's plan, this is about the aircraft they just committed.
      const isOrbit = draftRef.current.missionType === 'orbit';
      const orbitNote = isOrbit ? orbitLaunchWarning(draftRef.current.orbit) : null;
      const description = [orbitNote, advisory].filter(Boolean).join(' ') || null;
      toast.success(
        isOrbit
          ? ORBIT_LAUNCHED_HEADLINE
          : `Launched, ${plural(draftRef.current.waypoints.length, TERMS.waypoint, TERMS.waypoints)} commanded`,
        description
          ? { ...openAction(missionId), description, duration: 10_000 }
          : openAction(missionId),
      );
      onCreatedRef.current(missionId);
    } catch (err) {
      // A refusal is the normal case here, not an exception: the GO/NO-GO gate says
      // no far more often than the server breaks. Show the server's reason verbatim
      // — except a timeout, which is not a refusal but an unknown, and says so in
      // the same words the queue panel's door uses.
      const message = isLaunchRequestTimeout(err)
        ? LAUNCH_TIMEOUT_MESSAGE
        : getApiErrorMessage(err, 'Launch failed');
      setLaunchError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [launchArmedAt, launchTargetId, createMission, saveRoute, openAction]);

  const create = useCallback(async () => {
    const currentGates = gatesRef.current;
    if (!currentGates.canCreate) {
      toast.error(currentGates.createBlocker ?? 'Cannot create this mission');
      return;
    }

    let departureIso: string | undefined;
    if (scheduledAtRef.current) {
      const iso = utcInputValueToIso(scheduledAtRef.current);
      if (!iso) {
        toast.error('Departure time is not a valid date and time');
        return;
      }
      departureIso = iso;
    }

    setSaving(true);
    try {
      const count = draftRef.current.waypoints.length;
      const newId = await createMission(departureIso);
      if (newId === null) {
        toast.warning(count > 0
          ? 'Mission created, but no id came back — the route was not saved and must be redrawn'
          : 'Mission created');
        onCreatedWithoutIdRef.current();
        return;
      }

      if (currentGates.routeWillPersist) {
        try {
          await saveRoute(newId);
          toast.success(`Mission created with ${count} waypoints`, openAction(newId));
        } catch (routeErr) {
          // The mission exists; only the route failed. Stay put and keep the drawn
          // route in state — navigating now is what would actually destroy it.
          setCreatedMissionId(newId);
          toast.error(isLaunchRequestTimeout(routeErr)
            ? `Mission created. ${COMMIT_TIMEOUT_MESSAGE}`
            : `Mission created, but the route failed to save: ${getApiErrorMessage(routeErr, 'unknown error')}`);
          return;
        }
      } else if (count > 0) {
        // A route that will not persist must never be reported as a clean create.
        toast.warning(
          `Mission created — the ${count === 1 ? 'single waypoint was' : 'route was'} not saved (${currentGates.routeWarning ?? 'invalid route'})`,
          openAction(newId),
        );
      } else {
        toast.success('Mission created', openAction(newId));
      }
      onCreatedRef.current(newId);
    } catch (err) {
      // A timeout is not a failure: the POST may have landed, and saying otherwise invites a
      // second press that creates a duplicate mission for one sortie.
      toast.error(isLaunchRequestTimeout(err) ? COMMIT_TIMEOUT_MESSAGE : getApiErrorMessage(err, 'Failed to create mission'));
    } finally {
      setSaving(false);
    }
  }, [createMission, saveRoute, openAction]);

  const disarmLaunch = useCallback(() => setLaunchArmedAt(null), []);

  /**
   * ANYTHING THAT CHANGES WHAT WOULD BE LAUNCHED DISARMS THE CONFIRM.
   *
   * Without this an operator can arm LAUNCH, drag a waypoint across the map, and
   * the second press commands a route they never confirmed. It lives in the hook
   * rather than on each surface precisely because it is a property of the armed
   * launch, not of the screen it is armed on — the canvas held it as a local
   * effect, and moving compose onto its own surface would otherwise have left it
   * behind.
   *
   * Keyed on the CONTENT of the plan, not object identity: the draft object is
   * rebuilt on every render, so identity would disarm continuously and the
   * confirm could never be reached.
   */
  // Each point's OWN altitude is part of the plan, not a detail of it: raising WP7 to 300 m
  // between the arm and the confirm changes what the aircraft would fly just as surely as
  // dragging it would, so it disarms too.
  // THE TYPE AND ITS PARAMETERS ARE PART OF THE PLAN TOO, and for an ORBIT they are most of it:
  // its radius IS the flight path. Without them an operator could arm LAUNCH on a 100 m circle,
  // change the box to 5000, and the confirm would command a 5 km circle nobody armed - the same
  // defect as dragging a waypoint after arming, over a much larger piece of airspace.
  const launchSignature = [
    whenMode,
    draft.droneId ?? '',
    draft.altitudeM,
    draft.missionType ?? '',
    draft.orbit?.radiusM ?? '',
    draft.orbit?.direction ?? '',
    draft.waypoints.map((wp) => `${wp.lat},${wp.lng},${wp.altitudeM ?? ''}`).join('|'),
  ].join('#');
  useEffect(() => {
    setLaunchArmedAt(null);
  }, [launchSignature]);

  return {
    saving,
    launchError,
    routeRescueMissionId: createdMissionId,
    launchRescueMissionId: createdMissionId === null ? launchTargetId : null,
    launchArmed: launchArmedAt !== null,
    create,
    launchNow,
    retryRoute,
    disarmLaunch,
  };
}
